import { gql } from '@apollo/client'

const FRAGMENT_ADDRESS_FIELDS = gql`
  fragment newOrderAddressFields on Address {
    city
    country
    id
    name
    nameAddition
    street
    streetAddition
    zipcode
  }
`

export const GET_EMPLOYEE = gql`
  ${FRAGMENT_ADDRESS_FIELDS}
  query getEmployee($id: ID!) {
    employee(id: $id) {
      id
      canOrderNewDevice
      canOrder
      canPurchase
      canUpgradeDevice
      office {
        address {
          ...newOrderAddressFields
        }
        locationName
      }
      isCYOD
      contact {
        firstName
        lastName
        email
        address {
          id
          city
          country
          name
          nameAddition
          street
          zipcode
        }
        type
      }
      exitDate
      externalReference
      limitationRules {
        limitationRule {
          active
          available
          pending
          type
        }
      }
      caseitems {
        totalCount
        caseItems {
          id
          assignedEmployee {
            id
            contact {
              id
              firstName
              lastName
            }
          }
          caseType
          created
          data {
            requestedRentCategory {
              deviceDefinition {
                id
                imageURL
                name
                productType
              }
            }
          }
          parentCaseItem {
            caseType
            created
            id
            status
          }
          status
        }
      }
      rentals {
        totalCount
        rentals {
          id
          deviceID
          startAt
          coPaymentAmount
          costCenter {
            name
          }
          isBARBContract
          isEarlyUpgradeEligible
          isUpgradeEligible
          isUpgradePending
          minimumContractDuration
          stocklistItem {
            id
            imei
          }
          deviceDefinition {
            id
            name
            imageURL
            productType
          }
          rentCategory {
            copaymentNet
            copaymentGross
            name
            upgradeAmount
            deviceDefinitions {
              totalCount
              deviceDefinitions {
                codename
                id
                imageURL
                manufacturer
                storage
                productType
              }
            }
          }
          rentalProlonging {
            validFrom
          }
        }
      }
      phonePlans {
        totalCount
        phonePlans {
          id
          startAt
          endAt
          simcardNumber
          phonePlanTariff {
            name
          }
        }
      }
      orderItems {
        orderItems {
          id
          created
          order {
            id
            status
          }
          product {
            id
            title
            deviceDefinition {
              id
              imageURL
            }
          }
        }
      }
      organisation {
        id
        isCYOD
        contact {
          id
          address {
            ...newOrderAddressFields
          }
        }
        emailConfig {
          activationEmails
        }
        fullName
        individualTermsEN
        individualTermsDE
        isPlusOrganisation
        logoImageURL
        portalContent {
          dashboardHeaderEN
          dashboardHeaderDE
          dashboardSubtitleEN
          dashboardSubtitleDE
          dashboardButtonEN
          dashboardButtonDE
          dashboardImageURL
          faqCardURLEN
          faqCardURLDE
        }
        shortName
        currency
        impactFactors {
          status
          factors {
            name
            value
            valueConverted
            converter
          }
        }
        fulfillmentProviderMapping {
          country
          fullUrl
        }
      }
      openNewDeviceCaseItems {
        deviceDefinition {
          codename
          manufacturer
          storage
        }
        rentCategory {
          id
          copaymentNet
          copaymentGross
          deviatingAdvertisementPrice
          descriptionDe
          descriptionEn
          name
          individualTerms
          displayedBasisAmount
          upgradeAmount
          isFixedBundle
          displaySortOrder
          deviceDefinitions {
            totalCount
            deviceDefinitions {
              codename
              id
              imageURL
              manufacturer
              storage
              productType
            }
          }
        }
      }
      rentCategories {
        rentcategories {
          deviceDefinitions {
            deviceDefinitions {
              productType
            }
          }
        }
      }
    }
  }
`

export const GET_EMPLOYEE_DASHBOARD = gql`
  query getEmployeeDashboard($id: ID!) {
    employee(id: $id) {
      id
      canOrderNewDevice
      canOrder
      canUpgradeDevice
      canPurchase
      caseitems {
        totalCount
        caseItems {
          id
          data {
            requestedRentCategory {
              deviceDefinition {
                id
                imageURL
                name
                productType
              }
            }
          }
          status
          created
          caseType
        }
      }
      limitationRules {
        limitationRule {
          available
          type
        }
      }
      orderItems {
        orderItems {
          id
          created
          order {
            id
            status
          }
          product {
            id
            title
            deviceDefinition {
              id
              imageURL
            }
          }
        }
      }
      rentals {
        rentals {
          id
          deviceID
          deviceDefinitionID
          forcePriceHide
          actions(isOrganisationView: false) {
            showExchangeAction
            showEarlyUpgradeAction
            showStandardUpgradeAction
            showLostOrStolenAction
            showReplaceBrokenDeviceAction
            showUpgradeAction
            showCancelAction
            showReturnAction
            showReassignDeviceAction
            showDeviceProlongingAction
            showPurchaseDeviceAction
            showRepairUpgradeAction
            showReplaceFromDepotAction
            showReplacementOrderFlow
            showEarlyUpgradePenaltyAmount
          }
          alerts(isOrganisationView: false) {
            showUpgradePossibleAlert
            showLostDeviceReminderAlert
            showEarlyUpgradePossibleAlert
            showProlongingPossibleAlert
            showOpenLostDeviceAlert
            showOpenRepairDeviceAlert
            showOpenCancelDeviceAlert
          }
          minimumContractDuration
          minimumContractDurationDate
          startAt
          endAt
          earlyUpgradeDate
          earliestContractTermination
          isEarlyUpgradeEligible
          amount
          coPaymentAmount
          isBARBContract
          isUpgradeEligible
          isUpgradePending
          isProlongingEligible
          isReplacementDeviceAvailableInDepot
          isLostStolenNoReplacementEnabled
          newDeviceDate
          upgradeAmount
          poRcn
          customClientReference
          billingIdentifier
          rentalProlonging {
            validFrom
            validTill
          }
          employee {
            id
            phonePlans {
              phonePlans {
                endAt
                id
                simcardNumber
                startAt
                phoneNumber
                phonePlanTariff {
                  name
                  logo
                }
              }
            }
            office {
              locationName
              address {
                city
                country
                id
                name
                nameAddition
                street
                zipcode
                streetAddition
              }
              organisation {
                shortName
              }
            }
            contact {
              id
              firstName
              email
              lastName
              phoneNumber
              type
              address {
                city
                country
                id
                name
                nameAddition
                street
                zipcode
                streetAddition
              }
            }
          }
          organisation {
            id
            fullName
            shortName
            individualTermsEN
            individualTermsDE
            contact {
              address {
                city
                country
                id
                name
                nameAddition
                street
                zipcode
                streetAddition
              }
            }
          }
          stocklistItem {
            id
            imei
            imei2
            depot {
              locationName
            }
            eid
            serialNumber
          }
          stocklistItemHistory {
            totalCount
            stocklistItems {
              id
              imei
              serialNumber
              deviceDefinition {
                name
                imageURL
              }
            }
          }
          costCenter {
            name
          }
          rentCategory {
            id
            copaymentNet
            copaymentGross
            descriptionDe
            descriptionEn
            deviceDefinitions {
              deviceDefinitions {
                id
                codename
                color
                imageURL
                manufacturer
                storage
              }
            }
            displaySortOrder
            name
          }
          rentalProlongingPrice {
            basisAmount
            copaymentAmount
            duration
            startDate
            endDate
            prolongAfterInMonths
          }
          parent {
            id
            deviceDefinition {
              name
              imageURL
            }
          }
          children {
            id
            deviceDefinition {
              name
              imageURL
            }
          }
          deviceDefinition {
            id
            name
            codename
            storage
            color
            imageURL
            manufacturer
            productType
          }
        }
      }
      rentCategories {
        rentcategories {
          deviceDefinitions {
            deviceDefinitions {
              productType
            }
          }
        }
      }
    }
  }
`
